/**
 * features/bundle/model/useProductBundles
 *
 * Инкапсулирует всю логику блока «Собрать набор» для страницы/модала продукта:
 *  — загрузка данных через /products/detail/:id
 *  — управление состоянием списка бандлов
 *  — открытие BundleBuilderOverlay
 *
 * SRP: виджет не знает об API и внутреннем стейте бандлов.
 */

import { ref, computed, type Ref } from 'vue';
import { consola } from 'consola';
import type { ProductBundle } from '@entities/product';
import apiProductInstance from '~/entities/product';

const logger = consola.withTag('useProductBundles');

interface UseProductBundlesOptions {
  /** ID текущего варианта продукта. */
  productId: Ref<number | string | null | undefined>;
  /** ID выбранного филиала. */
  branchId: Ref<string | null>;
  /** true, если текущий продукт является комбо — для комбо бандлы не загружаются. */
  isCombo: Ref<boolean>;
}

export function useProductBundles({ productId, branchId, isCombo }: UseProductBundlesOptions) {
  const bundles = ref<ProductBundle[]>([]);
  const showBundles = ref(false);
  const showBundleBuilder = ref(false);
  const selectedBundle = ref<ProductBundle | null>(null);
  const isLoading = ref(false);

  const openBundleBuilder = (bundle: ProductBundle) => {
    selectedBundle.value = bundle;
    showBundleBuilder.value = true;
  };

  const load = async () => {
    if (isCombo.value) {
      showBundles.value = false;
      bundles.value = [];
      return;
    }

    const pid = productId.value;
    const bid = branchId.value;
    if (!pid || !bid) return;

    isLoading.value = true;
    try {
      const detail = await apiProductInstance.getProductDetail(Number(pid), Number(bid));
      showBundles.value = detail.show_bundles && detail.bundles.length > 0;
      bundles.value = detail.bundles ?? [];
    } catch (error: unknown) {
      const err = error as { status?: number; statusCode?: number; message?: string };
      if (err?.status === 404 || err?.statusCode === 404 || String(err?.message).includes('404')) {
        logger.debug('products/detail not found, hiding bundles section');
      } else {
        logger.warn('Failed to load product detail:', error);
      }
      showBundles.value = false;
      bundles.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const hasBundles = computed(() => showBundles.value && bundles.value.length > 0);

  return {
    bundles,
    hasBundles,
    showBundleBuilder,
    selectedBundle,
    isLoading,
    openBundleBuilder,
    load,
  };
}
